import type {
  ClinicianWorkbenchDvtRiskFactorKey,
  ClinicianWorkbenchPatientRecordV1,
  ClinicianWorkbenchSurgeryTypeCategory,
} from "@amplecare/contracts";

export interface ScoreLine {
  key: string;
  label: string;
  points: number | null;
}

export interface ComputedScore {
  totalPoints: number | null;
  lines: ScoreLine[];
  missingInputs: string[];
}

export function computeAgeYears(
  dateOfBirth: string | null,
  onDate: string | null,
): number | null {
  if (!dateOfBirth) {
    return null;
  }

  const birth = new Date(`${dateOfBirth}T00:00:00Z`);
  const reference = onDate
    ? new Date(`${onDate}T00:00:00Z`)
    : new Date();

  if (Number.isNaN(birth.getTime()) || Number.isNaN(reference.getTime())) {
    return null;
  }

  let age = reference.getUTCFullYear() - birth.getUTCFullYear();
  const monthDelta = reference.getUTCMonth() - birth.getUTCMonth();

  if (
    monthDelta < 0 ||
    (monthDelta === 0 && reference.getUTCDate() < birth.getUTCDate())
  ) {
    age -= 1;
  }

  return age;
}

export function computeBmi(
  heightInches: number | null,
  weightPounds: number | null,
): number | null {
  if (!heightInches || !weightPounds || heightInches <= 0) {
    return null;
  }

  return Math.round(((weightPounds * 703) / (heightInches * heightInches)) * 10) / 10;
}

export function dvtAgePoints(ageYears: number): number {
  if (ageYears < 16) {
    return 0;
  }

  if (ageYears > 75) {
    return 3;
  }

  if (ageYears >= 60) {
    return 2;
  }

  if (ageYears >= 40) {
    return 1;
  }

  return 0;
}

export function dvtBmiPoints(bmi: number): number {
  if (bmi > 50) {
    return 3;
  }

  if (bmi >= 40) {
    return 2;
  }

  if (bmi >= 25) {
    return 1;
  }

  return 0;
}

export function dvtDurationPoints(durationMinutes: number): number {
  if (durationMinutes < 30) {
    return 0;
  }

  if (durationMinutes > 240) {
    return 3;
  }

  if (durationMinutes > 120) {
    return 2;
  }

  return 1;
}

// Band labels mirror the printed "DVT RISK ASSESSMENT SCORING" key
// (ONE MNET.pdf, DVT Assessment page 2 of 2) — the sheet nurses sign against.
export const dvtScoringKeyText = {
  age: {
    note: "Limit risk factor assessment to patients age 16 or older.",
    bands: ["1, age from 40 to 59", "2, age from 60 to 75", "3, age greater than 75"],
  },
  bmi: {
    note: null as string | null,
    bands: ["1, BMI from 25 to 39", "2, BMI from 40 to 50", "3, BMI greater than 50"],
  },
  duration: {
    note: "Limit risk factor assessment to procedures scheduled 30 minutes or longer.",
    bands: [
      "1, duration of surgery from 30 to 120 minutes",
      "2, duration of surgery from 121 to 240 minutes",
      "3, duration of surgery greater than 240 minutes",
    ],
  },
  surgeryType: [
    "4 points: Lower Extremity Arthroscopy (in joint)",
    "4 points: Lower Extremity Arthroplasty (joint replacement, including partial) (any joint)",
    "4 points: Fracture Surgery of the Leg",
    "2 points: Upper Extremity Arthroscopy (in joint)",
    "2 points: Upper Extremity Arthroplasty (joint replacement, including partial) (any joint)",
    "2 points: Upper Extremity Fracture Surgery",
    "2 points: Laparoscopy",
    "2 points: Placement of Central Venous Access",
    "5 points: Great Veins of the Leg",
  ],
};

const dvtSurgeryTypePoints: Record<
  ClinicianWorkbenchSurgeryTypeCategory,
  number
> = {
  "lower-extremity-arthroscopy": 4,
  "lower-extremity-arthroplasty": 4,
  "leg-fracture-surgery": 4,
  "upper-extremity-arthroscopy": 2,
  "upper-extremity-arthroplasty": 2,
  "upper-extremity-fracture-surgery": 2,
  laparoscopy: 2,
  "central-venous-access": 2,
  "great-veins-of-leg": 5,
  other: 0,
};

interface DvtRiskFactorRubricEntry {
  key: ClinicianWorkbenchDvtRiskFactorKey;
  label: string;
  points: number;
  womenOnly?: boolean;
}

export const dvtRiskFactorRubric: DvtRiskFactorRubricEntry[] = [
  { key: "bloodClottingDisorders", label: "Blood clotting/blood disorders", points: 5 },
  { key: "cancer", label: "Cancer", points: 5 },
  { key: "currentlyPregnant", label: "Currently pregnant", points: 5, womenOnly: true },
  {
    key: "postPartumWithin30Days",
    label: "Post-partum within 30 days of upcoming procedure",
    points: 5,
    womenOnly: true,
  },
  {
    key: "miscarriageWithin30Days",
    label: "Miscarriage within 30 days of upcoming procedure",
    points: 5,
    womenOnly: true,
  },
  { key: "oralContraceptives", label: "Oral contraceptives", points: 1, womenOnly: true },
  {
    key: "hormoneReplacementTherapy",
    label: "Hormone replacement therapy",
    points: 1,
    womenOnly: true,
  },
  {
    key: "twoOrMoreMiscarriages",
    label: "History of 2 or more spontaneous miscarriages",
    points: 1,
    womenOnly: true,
  },
  {
    key: "surgeryWithin30Days",
    label: "Surgery in an operating room within 30 days of upcoming procedure",
    points: 1,
  },
  { key: "varicoseVeins", label: "Varicose veins", points: 1 },
  { key: "colitisOrCrohns", label: "Ulcerative Colitis or Crohn's Disease", points: 1 },
  {
    key: "longTripPlanned",
    label: "Long car trip or long airplane flight (> 4 hours) planned within 7 days after upcoming procedure",
    points: 1,
  },
  {
    key: "centralVenousCatheter",
    label: "Central venous catheter or port currently in place",
    points: 2,
  },
  {
    key: "immobilizingCastWithin30Days",
    label: "Lower extremity immobilizing cast within 30 days of procedure",
    points: 2,
  },
  {
    key: "bedConfined72Hours",
    label: "Confined to a bed for the last 72 hours or longer",
    points: 2,
  },
];

export interface DvtScoreResult extends ComputedScore {
  vteRateText: string | null;
  recommendScds: boolean;
}

export function computeDvtScore(
  record: ClinicianWorkbenchPatientRecordV1,
): DvtScoreResult {
  const missingInputs: string[] = [];
  const lines: ScoreLine[] = [];

  const age = computeAgeYears(
    record.patient.dateOfBirth,
    record.case.dateOfService,
  );
  const agePoints = age == null ? null : dvtAgePoints(age);

  if (age == null) {
    missingInputs.push("Date of birth");
  }

  lines.push({
    key: "age",
    label: age == null ? "Age" : `${age} years of age`,
    points: agePoints,
  });

  const bmi = computeBmi(
    record.patient.heightInches,
    record.patient.weightPounds,
  );
  const bmiPoints = bmi == null ? null : dvtBmiPoints(bmi);

  if (bmi == null) {
    missingInputs.push("Height/weight (BMI)");
  }

  lines.push({
    key: "bmi",
    label: bmi == null ? "BMI" : `BMI: ${bmi}`,
    points: bmiPoints,
  });

  const duration = record.case.surgeryDurationMinutes;
  const durationPoints = duration == null ? null : dvtDurationPoints(duration);

  if (duration == null) {
    missingInputs.push("Duration of surgery");
  }

  lines.push({
    key: "duration",
    label:
      duration == null
        ? "Duration of surgery (including anesthesia time)"
        : `Duration of surgery: ${duration} minutes`,
    points: durationPoints,
  });

  const surgeryType = record.case.surgeryTypeCategory;
  const surgeryTypePoints =
    surgeryType == null ? null : dvtSurgeryTypePoints[surgeryType];

  if (surgeryType == null) {
    missingInputs.push("Type of surgery");
  }

  lines.push({
    key: "surgery-type",
    label: "Type of surgery",
    points: surgeryTypePoints,
  });

  const isFemale = record.patient.sex === "female";
  let riskFactorPoints = 0;
  let riskFactorsKnown = true;

  for (const entry of dvtRiskFactorRubric) {
    if (entry.womenOnly && !isFemale) {
      continue;
    }

    const value = record.clinical.dvtRiskFactors[entry.key];

    if (value == null) {
      riskFactorsKnown = false;
      missingInputs.push(entry.label);
      continue;
    }

    if (value) {
      riskFactorPoints += entry.points;
    }
  }

  lines.push({
    key: "risk-factors",
    label: "Intrinsic and miscellaneous risk factors",
    points: riskFactorsKnown ? riskFactorPoints : null,
  });

  const scoreable = lines.every((line) => line.points != null);
  const totalPoints = scoreable
    ? lines.reduce((total, line) => total + (line.points ?? 0), 0)
    : null;

  const vteRateText =
    totalPoints == null
      ? null
      : totalPoints <= 2
        ? "< 0.1%"
        : totalPoints <= 5
          ? "0.1-0.3%"
          : totalPoints <= 10
            ? "0.3-0.5%"
            : "Up to 1.2%";

  return {
    totalPoints,
    lines,
    missingInputs,
    vteRateText,
    recommendScds: totalPoints != null && totalPoints >= 6,
  };
}

interface FallRiskRubricEntry {
  key: string;
  label: string;
  points: number;
  isPresent: (record: ClinicianWorkbenchPatientRecordV1) => boolean | null;
}

const fallRiskRubric: FallRiskRubricEntry[] = [
  {
    key: "age",
    label: "Age — < 3 years or > 65 years",
    points: 3,
    isPresent: (record) => {
      const age = computeAgeYears(
        record.patient.dateOfBirth,
        record.case.dateOfService,
      );

      return age == null ? null : age < 3 || age > 65;
    },
  },
  {
    key: "dizziness",
    label: "Dizziness/Vertigo",
    points: 1,
    isPresent: (record) => record.clinical.basicHistory.dizzinessVertigo.answer,
  },
  {
    key: "impaired-mobility",
    label: "Impaired mobility",
    points: 2,
    isPresent: (record) => record.clinical.basicHistory.mobilityProblems.answer,
  },
  {
    key: "visual-impairment",
    label: "Visual impairment",
    points: 2,
    isPresent: (record) => record.clinical.basicHistory.visualImpairment.answer,
  },
  {
    key: "confusion",
    label: "Confusion",
    points: 3,
    isPresent: (record) => record.clinical.basicHistory.confusion.answer,
  },
  {
    key: "altered-elimination",
    label: "Altered elimination",
    points: 3,
    isPresent: (record) => record.clinical.basicHistory.alteredElimination.answer,
  },
  {
    key: "history-of-falls",
    label: "History of falls",
    points: 7,
    isPresent: (record) => record.clinical.basicHistory.historyOfFalls.answer,
  },
  {
    key: "home-meds",
    label: "At home meds/sedation",
    points: 2,
    isPresent: (record) =>
      record.clinical.basicHistory.medsCausingDizziness.answer,
  },
];

export interface FallRiskScoreResult extends ComputedScore {
  highRisk: boolean;
}

export function computeFallRiskScore(
  record: ClinicianWorkbenchPatientRecordV1,
): FallRiskScoreResult {
  const missingInputs: string[] = [];
  const lines: ScoreLine[] = fallRiskRubric.map((entry) => {
    const present = entry.isPresent(record);

    if (present == null) {
      missingInputs.push(entry.label);
    }

    return {
      key: entry.key,
      label: entry.label,
      points: present == null ? null : present ? entry.points : 0,
    };
  });

  const scoreable = lines.every((line) => line.points != null);
  const totalPoints = scoreable
    ? lines.reduce((total, line) => total + (line.points ?? 0), 0)
    : null;

  return {
    totalPoints,
    lines,
    missingInputs,
    highRisk: totalPoints != null && totalPoints >= 6,
  };
}

export interface ClinicalSignificanceItem {
  code: string;
  label: string;
}

function hasFindingCode(
  record: ClinicianWorkbenchPatientRecordV1,
  code: string,
): boolean {
  return Object.values(record.clinical.reviewOfSystems).some((system) =>
    system.findings.some((finding) => finding.code === code),
  );
}

export function computeClinicalSignificanceItems(
  record: ClinicianWorkbenchPatientRecordV1,
): ClinicalSignificanceItem[] {
  const items: ClinicalSignificanceItem[] = [];

  if (hasFindingCode(record, "fever-chills-muscle-pain")) {
    items.push({
      code: "recent-symptoms",
      label:
        "Symptoms in the Past 14 days: Fever (even low grade), Chills, Muscle pain",
    });
  }

  const age = computeAgeYears(
    record.patient.dateOfBirth,
    record.case.dateOfService,
  );
  const sleepApneaFactors: string[] = [];

  if (hasFindingCode(record, "snores-loudly")) {
    sleepApneaFactors.push("Snore loudly");
  }

  if (hasFindingCode(record, "tired-during-day")) {
    sleepApneaFactors.push("Tired during day");
  }

  if (
    hasFindingCode(record, "hypertension-controlled") ||
    hasFindingCode(record, "hypertension")
  ) {
    sleepApneaFactors.push("High blood pressure");
  }

  if (age != null && age > 50) {
    sleepApneaFactors.push("Age greater than 50");
  }

  if (record.patient.sex === "male") {
    sleepApneaFactors.push("Male");
  }

  if (sleepApneaFactors.length >= 3) {
    items.push({
      code: "sleep-apnea-screening",
      label: `Sleep Apnea Screening: ${sleepApneaFactors.join(", ")}`,
    });
  }

  if (hasFindingCode(record, "chf")) {
    items.push({ code: "chf", label: "Congestive heart failure (CHF)" });
  }

  if (hasFindingCode(record, "cad")) {
    items.push({ code: "cad", label: "Coronary Artery Disease (CAD)" });
  }

  if (hasFindingCode(record, "cold-flu-symptoms")) {
    items.push({
      code: "cold-flu",
      label: "Current symptoms of a cold, flu or illness",
    });
  }

  return items;
}
